import React, { useState } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import { useGetAllUsersQuery, useSendInvitationMutation, useAddUserMutation } from '../../services/api';
import UsersHeader from './UsersHeader';
import UserList from './UserList';
import AddUserDialogBox from './AddUserDialogBox';
import ErrorSnackbar from '../../components/ErrorSnackbar';

const Users = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const { role, company } = useSelector((state) => state.global);

    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [userData, setUserData] = useState({ name: '', email: '', country: '', phoneNumber: '', role: '' });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

    // API
    const { data: usersData, refetch: refetchUsers } = useGetAllUsersQuery(company);
    const [sendInvitation] = useSendInvitationMutation();
    const [addUserMutation] = useAddUserMutation();

    // Handle dailog box states
    const handleOpenDialog = () => setOpenDialog(true);
    const handleCloseDialog = () => setOpenDialog(false);

    // Hnalde input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUserData({
            ...userData,
            [name]: value,
        });
    };

    // Hanlde add user
    const handleAddUser = async () => {
        try {
            if (!userData.name || !userData.email || !userData.role) {
                showSnackbar('Please fill all fields', 'error');
                return;
            }

            setLoading(true);

            await addUserMutation({
                name: userData.name,
                email: userData.email,
                role: userData.role,
                company: company,
            }).unwrap();

            await sendInvitation({
                name: userData.name,
                email: userData.email,
            });

            showSnackbar('User added successfully', 'success');
            refetchUsers();
        } catch (error) {
            console.error('Error adding user:', error);

            if (error?.status === 400 && error?.data?.message === 'User with this email already exists') {
                showSnackbar('User already exists for another account', 'error');
            } else if (error?.status === 403 && error?.data?.message === 'User limit reached. Cannot add more users.') {
                showSnackbar('User limit reached. Cannot add more users.', 'error');
            } else {
                showSnackbar('Failed to add user', 'error');
            }
        } finally {
            setLoading(false);
            handleCloseDialog();
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    return (
        <Box m={isSmallScreen ? '1rem' : '1rem 2.5rem'}>
            <UsersHeader role={role} handleOpenDialog={handleOpenDialog} />
            <UserList usersData={usersData} showSnackbar={showSnackbar} refetchUsers={refetchUsers} isSmallScreen={isSmallScreen} />
            <AddUserDialogBox open={openDialog} handleCloseDialog={handleCloseDialog} userData={userData} handleInputChange={handleInputChange} handleAddUser={handleAddUser} loading={loading} />
            <ErrorSnackbar open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} />
        </Box>
    );
};

export default Users;
